/**
 * Build the payload that the dashboard editor posts back to the backend.
 */

/**
 * Convert one editor positioning record back to the storage format.
 */
function createWidgetPositioningPayload(widgetPositioning, breakpointId, widgetIndex) {
    return {
        breakpointId,
        orderIndex: Number(widgetPositioning.orderIndex ?? widgetIndex),
        column: Number(widgetPositioning.offset ?? 0) + 1,
        row: Number(widgetPositioning.row ?? 1),
        columnSpan: Number(widgetPositioning.colspan ?? 12),
        rowSpan: Number(widgetPositioning.rowSpan ?? 1),
        hidden: Boolean(widgetPositioning.hidden),
    };
}

/**
 * Resolve the positioning for one widget inside one breakpoint.
 */
function findWidgetPositioning(breakpoint, widgetReferenceId) {
    if (!Array.isArray(breakpoint?.widgetPositioning)) {
        return {};
    }

    return breakpoint.widgetPositioning.find((positioning) => positioning.referenceId === widgetReferenceId) || {};
}

/**
 * Convert one editor widget back to the saved dashboard structure.
 */
function createWidgetPayload(provider, widget, widgetIndex, breakpoints) {
    const positioning = [];

    for (const breakpoint of breakpoints) {
        const widgetPositioning = findWidgetPositioning(breakpoint, widget.referenceId);

        positioning.push(
            createWidgetPositioningPayload(widgetPositioning, breakpoint.id, widgetIndex)
        );
    }

    return {
        id: widget.id,
        referenceId: widget.referenceId,
        type: widget.type,
        name: widget.name,
        tabId: widget.tabId || provider.getDefaultDashboardTab().id,
        orderIndex: widgetIndex,
        data: provider.normalizeWidgetDataForStorage(widget),
        positioning,
    };
}

/**
 * Convert the editor breakpoints back to the persisted dashboard breakpoints.
 */
function createBreakpointPayloads(breakpoints) {
    const payloadBreakpoints = [];

    for (let breakpointIndex = 0; breakpointIndex < breakpoints.length; breakpointIndex += 1) {
        const breakpoint = breakpoints[breakpointIndex];

        payloadBreakpoints.push({
            id: breakpoint.id,
            isOriginal: Boolean(breakpoint.isOriginal || breakpointIndex === 0),
        });
    }

    return payloadBreakpoints;
}

/**
 * Convert the editor widgets back to the persisted dashboard widgets.
 */
function createWidgetPayloads(provider, widgets, breakpoints) {
    const payloadWidgets = [];

    for (let widgetIndex = 0; widgetIndex < widgets.length; widgetIndex += 1) {
        const widget = widgets[widgetIndex];

        payloadWidgets.push(
            createWidgetPayload(provider, widget, widgetIndex, breakpoints)
        );
    }

    return payloadWidgets;
}

/**
 * Build the complete request payload for saving a dashboard.
 */
export function buildDashboardPayloadForSave(provider, breakpoints, widgets) {
    return {
        ownerType: provider.getOwnerType(),
        ownerId: provider.getOwnerId(),
        dashboard: {
            id: provider.getDashboardId(),
            title: provider.title,
            tabs: provider.normalizeDashboardTabs(provider.dashboardTabs),
            data: {},
            breakpoints: createBreakpointPayloads(breakpoints),
            widgets: createWidgetPayloads(provider, widgets, breakpoints),
        },
    };
}
